import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { parseNumericMoneyInput } from './echartsConfig';
import { getCurrency as currencySelector } from 'utils/redux/reducer/authentication-slice';

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function currencySymbolFromContext(currencyContext) {
  if (currencyContext?.currencySymbol) return currencyContext.currencySymbol;
  const currency = currencyContext?.currency || 'INR';
  const locale = currencyContext?.currencyLocale?.replace(/_/g, '-') || 'en-IN';
  try {
    return (
      new Intl.NumberFormat(locale, { style: 'currency', currency })
        .formatToParts(0)
        .find(part => part.type === 'currency')?.value || currency
    );
  } catch {
    return currency;
  }
}

function formatWithCurrencyContext(value, currencyContext) {
  const currencySymbol = currencySymbolFromContext(currencyContext);
  const locale = currencyContext?.currencyLocale?.replace(/_/g, '-') || 'en-IN';
  const n = Number(value);
  if (!Number.isFinite(n)) return `${currencySymbol}0`;
  return `${currencySymbol}${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n)}`;
}

function normalizeKpiDisplayValue(value, currencyContext) {
  if (value == null || value === '') return '—';
  if (typeof value === 'number') return formatWithCurrencyContext(value, currencyContext);
  if (typeof value !== 'string') return value;

  const currencySymbol = currencySymbolFromContext(currencyContext);
  const escapedSymbol = escapeRegExp(currencySymbol);
  const moneyTokenPattern = new RegExp(
    `(?:${escapedSymbol}|₹|rs\\.?|inr)\\s*[\\d,.]+\\s*(?:lac|lakh|crore|cr|l|k)?`,
    'gi',
  );

  return value.replace(moneyTokenPattern, token => {
    const parsed = parseNumericMoneyInput(token);
    return parsed == null ? token : formatWithCurrencyContext(parsed, currencyContext);
  });
}

/**
 * Flat KPI strip — no mini-card borders, divider-separated.
 * High-density executive style.
 * @param {{ items?: { label: string, value: any }[], columns?: number }} props
 */
const ReportKpiStrip = (props) => {
  const items = props.items || [];
  const columns = props.columns;
  const currencyContext = useSelector(currencySelector);
  if (!items.length) return null;
  const minCardWidth = columns ? `${Math.floor(100 / columns) - 1}%` : '90px';

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '1px',
        mb: '10px',
        flexShrink: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        pb: 0.25,
        '&::-webkit-scrollbar': { height: 2 },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'action.disabled',
          borderRadius: 2,
        },
      }}>
      {items.map((item, i) => {
        const displayValue = normalizeKpiDisplayValue(item.value, currencyContext);
        return (
          <Box
            key={i}
            sx={{
              minWidth: minCardWidth,
              flex: '1 0 auto',
              px: 1,
              py: 0.5,
              borderRight: i < items.length - 1 ? '1px solid #E5E7EB' : 'none',
            }}>
            <Typography
              noWrap
              title={item.label}
              sx={{
                display: 'block',
                fontWeight: 600,
                fontSize: '9px',
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                lineHeight: 1.2,
                color: '#9CA3AF',
              }}>
              {item.label}
            </Typography>
            <Typography
              noWrap
              title={typeof displayValue === 'string' ? displayValue : undefined}
              sx={{
                fontWeight: 700,
                fontSize: '15px',
                mt: '2px',
                lineHeight: 1.2,
                fontVariantNumeric: 'tabular-nums',
                color: '#111827',
              }}>
              {displayValue}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

function formatKpiStripValue(value) {
  if (value == null || value === '') return '—';
  return String(value);
}

/**
 * Converts a backend kpiMap (object with label→value entries) into the items array
 * expected by ReportKpiStrip.
 */
export function kpiMapToItems(kpiMap) {
  if (!kpiMap || typeof kpiMap !== 'object') return null;
  const entries = Object.entries(kpiMap);
  if (entries.length === 0) return null;
  return entries.map(([label, value]) => ({ label, value: formatKpiStripValue(value) }));
}

export default ReportKpiStrip;
